/**
 * ART RESOLUTION — the design pack is the authority, not this file.
 *
 * WHY THIS WAS REWRITTEN (26 Jul, round-4 handoff):
 * The previous version carried hand-typed allowlists (`SHIPPED_BADGES`,
 * `SHIPPED_GAFFERS`, `SHIPPED_REWARD_CRATES`) and returned `undefined` for any
 * id outside them, which callers rendered as a dashed `AWAITING` box. That
 * inverted the actual rule. Consequences, measured:
 *   - 13 of the 14 `rewards/` files had never been copied into the repo, so the
 *     allowlist "correctly" reported art missing that simply hadn't been moved.
 *   - I then reasoned FROM that truncated folder and wrote, in a commit, that
 *     "no `_open` variant exists for any vehicle". rv_v3_open, rv_v4_open,
 *     rv_v5_open and all five `po_*` payout objects existed the whole time.
 *
 * The handoff rule (00-GLOBAL/ASSETS.md §2) is the opposite and is now the law
 * here: **every image in the design is a real file. If it does not load, the
 * PATH is wrong — fix the path. Never fall back to an icon, an emoji, an SVG
 * redraw, or a coloured block.** Those "imposter assets" are explicitly
 * rejected.
 *
 * So this module no longer decides what exists. It maps an id to its canonical
 * path and returns it. The only ids that may resolve to nothing are the three
 * genuinely un-drawn slots (ASSETS.md §4), enumerated below and nowhere else.
 */

/** The /public/assets/art/ pack directories (mirrors ASSETS.md). */
export type ArtKind = "badges" | "gaffers" | "rewards" | "cabinet" | "locker" | "var" | "ea" | "blg";

/**
 * Canonical URL for a pack file.
 *
 * The whole pack is webp as of 27 Jul (scripts/optimise-art.py). It shipped as
 * source-resolution PNG/JPG — 34MB, single backdrops at 1.5-2.1MB — and Vite
 * copies public/ wholesale, so every byte reached every device. That is what BK's
 * device test saw as "background assets load slowly".
 *
 * `ext` is kept as a parameter rather than hard-coded: a future pack may ship a
 * format webp cannot carry, and a resolver that cannot express that is a resolver
 * someone works around.
 */
export function artUrl(kind: ArtKind, id: string, ext = "webp"): string {
  return `/assets/art/${kind}/${id}.${ext}`;
}

/**
 * The ONLY assets awaiting art (ASSETS.md §4). These three keep a labelled
 * slot; nothing else in the app may render a placeholder. Adding to this list
 * is a design decision, not an implementation convenience.
 */
export const AWAITING_ART: ReadonlySet<string> = new Set([
  "po_p5", // gold ticket
  "env_stadium_hero", // stadium hero environment
  "av_support", // bespoke support-agent avatar
]);

export function isAwaitingArt(id: string): boolean {
  return AWAITING_ART.has(id);
}

// ── identity ────────────────────────────────────────────────────────────────

/** COLOURS badge, e.g. "badge_b4_std" | "badge_b1_rare". */
export function badgeArt(badgeId: string): string {
  return artUrl("badges", badgeId);
}

/** Coach avatar (folder keeps its legacy `gaffers/` name — users never see it;
 *  user-facing copy says "coach", never "gaffer" — COPY.md). */
export function coachArt(avatarId: string): string {
  return artUrl("gaffers", avatarId);
}

// ── locker room ─────────────────────────────────────────────────────────────

/**
 * Badge → kit palette (ASSETS.md §3, verbatim):
 *   b1/b3/b6 → gold · b2 → red · b4/b7 → green · b5/b8/b9/b10 → blue
 */
const KIT_BY_BADGE_NUMBER: Record<number, "gold" | "red" | "green" | "blue"> = {
  1: "gold", 3: "gold", 6: "gold",
  2: "red",
  4: "green", 7: "green",
  5: "blue", 8: "blue", 9: "blue", 10: "blue",
};

/** The COLOURS number out of any badge id shape: badge_b4_std | b4 | 4 → 4. */
export function badgeNumberOf(badgeId: string): number {
  const m = /(?:^|_)b?(\d{1,2})(?:_|$)/i.exec(badgeId) ?? /(\d{1,2})/.exec(badgeId);
  const n = m ? Number.parseInt(m[1] as string, 10) : NaN;
  return Number.isFinite(n) && n >= 1 && n <= 10 ? n : 1;
}

export function kitPaletteFor(badgeId: string): "gold" | "red" | "green" | "blue" {
  return KIT_BY_BADGE_NUMBER[badgeNumberOf(badgeId)] ?? "blue";
}

/**
 * The locker room ENVIRONMENT recoloured by the worn badge — i.e. FIRST-NIGHT
 * S5's "hanging kit in the player's chosen badge colours".
 *
 * These are 720x1290, identical to hub-base/hub-complete/ceremony: full-screen
 * room renders, NOT garment cutouts. Verified by decoding them off production —
 * the name reads like a jersey and it is not one. Use as a background layer.
 */
// webp, not png (27 Jul). The locker pack shipped as eleven 720x1290 PNGs at
// ~1.5MB each — 11.7MB of backdrops on a PWA with a load budget, which is what
// BK's device saw as "background assets load slowly". Re-encoded to 287KB total
// at the same pixel dimensions; the art is unchanged, only the container.
export function kitArt(badgeId: string): string {
  return artUrl("locker", `kit-${kitPaletteFor(badgeId)}`);
}

/**
 * The kit GARMENT alone, for the change-colours slot and rail. 300x525-750 with
 * per-colour heights — tight-cropped cutouts, which is why the height varies.
 * This is the one that belongs in a small slot; kitArt() would shrink an entire
 * room into it.
 */
export function kitThumbArt(badgeId: string): string {
  return artUrl("locker", `kitthumb-${kitPaletteFor(badgeId)}`);
}

/** The Locker Room environment. THIS is what `AWAITING: ENV_LOCKER` was hiding
 *  — the file has been on disk the whole time. `complete` is the fully-dressed
 *  room; `base` is the pre-identity state. */
export function lockerEnvArt(complete = true): string {
  return artUrl("locker", complete ? "hub-complete" : "hub-base");
}

/** The identity-claim ceremony backdrop. */
export function lockerCeremonyArt(): string {
  return artUrl("locker", "ceremony");
}

// ── rewards (MOMENTS) ───────────────────────────────────────────────────────

/** SILVERWARE vehicle ids, rv_v1..rv_v5 (ASSETS.md). */
export type VehicleSlot = "rv_v1" | "rv_v2" | "rv_v3" | "rv_v4" | "rv_v5";

/**
 * Which visual states each vehicle actually ships. v1/v2 are trophies (hero,
 * one with a glow pass); v3/v4/v5 are chests/packs that open. Taken from the
 * pack contents, not guessed.
 */
const VEHICLE_STATES: Record<VehicleSlot, ReadonlyArray<"closed" | "open" | "hero" | "glow">> = {
  rv_v1: ["hero", "glow"],
  rv_v2: ["hero"],
  rv_v3: ["closed", "open"],
  rv_v4: ["closed", "open"],
  rv_v5: ["closed", "open"],
};

export function vehicleHasState(slot: VehicleSlot, state: "closed" | "open" | "hero" | "glow"): boolean {
  return VEHICLE_STATES[slot]?.includes(state) ?? false;
}

/** Sealed vehicle art; falls back to the hero render for trophy-type vehicles
 *  that have no closed state (v1/v2 are not crates). */
export function vehicleSealedArt(slot: VehicleSlot): string {
  return artUrl("rewards", vehicleHasState(slot, "closed") ? `${slot}_closed` : `${slot}_hero`);
}

/** Burst/open vehicle art, same fallback rule. */
export function vehicleOpenArt(slot: VehicleSlot): string {
  return artUrl("rewards", vehicleHasState(slot, "open") ? `${slot}_open` : `${slot}_hero`);
}

/** Payout objects: what actually came out of the crate. */
export type PayoutObject = "coins" | "coin" | "boost" | "trophy" | "cabinet" | "airtime";
const PAYOUT_FILE: Record<PayoutObject, string> = {
  coins: "po_p1_coins",
  coin: "po_p2_coin",
  boost: "po_p3_boost",
  cabinet: "po_p4_cabinet",
  trophy: "po_p4_trophy",
  // Added 27 Jul: the Vault's airtime tile. SCREEN 09 called for an outline
  // SIM icon that the pack did not ship and ASSETS.md did not list as a gap —
  // so nothing flagged it and the tile sat empty on the card carrying the most
  // real-world value in the locker.
  airtime: "po_p6_airtime",
};

export function payoutArt(object: PayoutObject): string {
  return artUrl("rewards", PAYOUT_FILE[object]);
}

// ── environments ────────────────────────────────────────────────────────────

/**
 * EA FC host-flow screenshots (SPEC 04 W2 + the how-to-host list).
 *
 * `step1..step4` shipped in the round-3 AND round-4 handoffs and were recorded
 * in ASSETS.md — and never carried into the app, so the wizard rendered
 * "AWAITING: ea_slot_wizard_loop" on a real device for weeks. The manifest knew;
 * nothing checked the manifest against what the screens actually resolve.
 *
 * Re-encoded to webp at 640px (2x the 320px W2 loop, its largest use): the source
 * PNGs were 1600px and up to 1.4MB each — 3.5MB of screenshots for a PWA with a
 * load budget, to render at most 320 CSS px wide.
 */
export type EaHostStep = 1 | 2 | 3 | 4;

export function eaHostStepArt(step: EaHostStep): string {
  return artUrl("ea", `step${step}`);
}

export function cabinetEnvArt(alt = false): string {
  return artUrl("cabinet", alt ? "env-alt" : "env-primary");
}

export function varBoothArt(alt = false): string {
  return artUrl("var", alt ? "booth-alt" : "booth-primary");
}

// ── back-compat shim ────────────────────────────────────────────────────────

/**
 * @deprecated Callers should use the specific resolver (badgeArt/coachArt/…).
 * Retained so the round-4 refactor can land screen-by-screen rather than as one
 * unreviewable change. Unlike the old version this NEVER returns undefined for
 * a real id — a badge/coach id always resolves to its file.
 */
export function catalogueArt(id: string): string | undefined {
  if (isAwaitingArt(id)) return undefined;
  if (id.startsWith("badge_")) return badgeArt(id);
  if (id.startsWith("av_")) return coachArt(id);
  return undefined;
}
